import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
  getPaymentInstructions, reportPayment, listMyPayments, createCheckout, formatMXN, getStore, getUser, getSubscriptionStatus,
} from "@/lib/api";
import DashNav from "@/components/chalann/DashNav";
import { PlanCard } from "@/components/chalann/PlanCard";
import { Building2, Copy, ArrowRight, Check, Clock, XCircle, CreditCard, Info, Landmark } from "lucide-react";

const STATUS = {
  pending:   { label: "En revisión", color: "text-[#ccff00]", bg: "bg-[#ccff00]/10 border-[#ccff00]/30", icon: Clock },
  confirmed: { label: "Confirmado", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", icon: Check },
  rejected:  { label: "Rechazado", color: "text-red-400", bg: "bg-red-500/10 border-red-500/30", icon: XCircle },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function Copiable({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1600); toast.success("Copiado"); }
    catch { toast.error("No se pudo copiar"); }
  };
  return (
    <button onClick={copy} className="inline-flex items-center gap-2 px-2 py-1 rounded-lg border border-white/10 hover:border-[#ccff00]/50 hover:text-[#ccff00] text-white/70 transition-colors" data-testid={testId}>
      {copied ? <Check size={12} /> : <Copy size={12} />}
      <span className="text-[10px] font-mono uppercase tracking-widest">{copied ? "Copiado" : "Copiar"}</span>
    </button>
  );
}

export default function Pagar() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [payments, setPayments] = useState([]);
  const [plan, setPlan] = useState(() => params.get("plan") || getStore()?.plan || "chalanito");
  const [method, setMethod] = useState("card"); // card | spei
  const [amount, setAmount] = useState("");
  const [ref, setRef] = useState("");
  const [depositor, setDepositor] = useState("");
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getStore()?.id) { nav("/setup"); return; }
    if (!getUser()?.id) { nav("/login"); return; }
    if (params.get("cancelled")) toast("Pago con tarjeta cancelado · puedes intentar de nuevo o pagar por SPEI");
  }, [nav, params]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([getPaymentInstructions(plan), listMyPayments(), getSubscriptionStatus()])
      .then(([i, p]) => {
        if (!alive) return;
        setInfo(i); setPayments(p.payments || []);
        setAmount(String(i.amount_mxn || ""));
        setDepositor((d) => d || getUser()?.name || "");
      })
      .catch(() => toast.error("No se pudieron cargar los datos"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [plan]);

  const payCard = async () => {
    setRedirecting(true);
    try {
      const r = await createCheckout(plan);
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago con tarjeta");
      setRedirecting(false);
    }
  };

  const submitSpei = async (e) => {
    e.preventDefault();
    if (!ref.trim()) { toast.error("Escribe la referencia del pago"); return; }
    setSending(true);
    try {
      const r = await reportPayment({
        plan,
        amount_mxn: Number(amount),
        reference: ref.trim(),
        depositor_name: depositor.trim(),
        paid_at: paidAt ? new Date(paidAt).toISOString() : null,
        notes: notes.trim(),
      });
      toast.success(r.message || "Pago reportado");
      setRef(""); setNotes("");
      setPayments((await listMyPayments()).payments || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo reportar"); }
    finally { setSending(false); }
  };

  if (!info) return (
    <main className="min-h-screen bg-[#050505] text-white">
      <DashNav />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 text-center text-white/40 text-sm">Cargando…</div>
    </main>
  );

  const sub = info.subscription;
  const chosen = info.plans.find((p) => p.key === plan) || info.plans[0];
  const currentName = (info.plans.find((p) => p.key === sub.plan) || {}).name || sub.plan;
  const isChange = sub.is_active && sub.plan !== plan;

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="pagar">
      <div className="grain-overlay" />
      <DashNav />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2">/ Pagar servicio</p>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none mb-2">
            {sub.is_active ? <>Tu plan <span className="text-[#ccff00]">{currentName}</span> está activo.</> : <>Activa tu <span className="text-[#ccff00]">plan</span>.</>}
          </h1>
          <p className="text-white/60 max-w-2xl" data-testid="pagar-status-copy">
            {sub.is_active
              ? `Vigente hasta ${fmtDate(sub.next_billing_at)}. Puedes renovar o cambiar de plan; el nuevo periodo empieza al terminar el actual.`
              : sub.is_expired
                ? "Tu periodo terminó. Renueva para seguir cobrando — tu inventario y ventas siguen guardados."
                : "Sin periodo de prueba: elige tu plan y la forma de pago para empezar a cobrar hoy."}
          </p>
        </motion.div>

        {/* Plan selector */}
        <section className="mt-8" data-testid="pagar-plans">
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-3">/ 1 · Elige tu plan</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {info.plans.map((p) => <PlanCard key={p.key} plan={p} selected={plan === p.key} onSelect={setPlan} />)}
          </div>
          {isChange && (
            <p className="mt-3 text-[12px] text-[#ccff00] font-mono uppercase tracking-widest" data-testid="pagar-change-note">
              Cambio de plan: al pagar, tu tienda pasa a {chosen.name}{chosen.key === "onepay" ? " (se apagan los comandos de voz e IA)" : " (se activan los comandos de voz con IA)"}.
            </p>
          )}
        </section>

        {/* Method */}
        <section className="mt-10">
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-3">/ 2 · Forma de pago</p>
          <div className="flex gap-2 mb-5">
            {[["card", "Tarjeta (Stripe)", CreditCard], ["spei", "Transferencia SPEI", Landmark]].map(([k, l, I]) => (
              <button key={k} onClick={() => setMethod(k)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-[11px] font-mono uppercase tracking-widest ${method === k ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white"}`} data-testid={`pay-method-${k}`}>
                <I size={13} /> {l}
              </button>
            ))}
          </div>

          {method === "card" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="relative bg-gradient-to-br from-[#0f0f0f] to-[#050505] border border-white/10 rounded-3xl p-6 md:p-8 overflow-hidden" data-testid="pay-card-box">
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#ccff00]/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <div className="chip"><CreditCard size={12} className="text-[#ccff00]" /> Pago seguro con Stripe</div>
                  <h2 className="font-display text-2xl md:text-3xl tracking-tighter mt-3">
                    {chosen.name} · <span className="text-[#ccff00]">${chosen.price_usd} USD</span> / {chosen.period_label}
                  </h2>
                  <p className="text-white/50 text-sm mt-2 max-w-lg">Tarjeta de crédito o débito. Tu banco hace la conversión a pesos. El plan se activa automáticamente en cuanto se confirma el cargo ({chosen.days} días).</p>
                </div>
                <button onClick={payCard} disabled={redirecting} className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-[#ccff00] hover:bg-[#d9ff33] text-black font-display text-xl tracking-tighter disabled:opacity-60 whitespace-nowrap" data-testid="pay-card-submit">
                  {redirecting ? "Abriendo Stripe…" : <>Pagar ${chosen.price_usd} USD <ArrowRight size={18} /></>}
                </button>
              </div>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-7 relative bg-gradient-to-br from-[#0f0f0f] to-[#050505] border border-white/10 rounded-3xl p-6 md:p-8 overflow-hidden" data-testid="pay-instructions">
                <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#ff5a00]/20 rounded-full blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="chip"><Building2 size={12} className="text-[#ccff00]" /> Transferencia SPEI · BBVA</div>
                  <h2 className="font-display text-2xl md:text-3xl tracking-tighter mt-3 mb-1">
                    Transfiere <span className="text-[#ccff00]">{formatMXN(info.amount_mxn)}</span>
                  </h2>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-6" data-testid="pay-fx-note">${info.amount_usd} USD × {info.usd_mxn_rate} MXN/USD · {chosen.name}</p>
                  <Row label="Beneficiario" value={info.beneficiary} testId="pay-beneficiary" />
                  <Row label="Banco" value={info.bank} testId="pay-bank" />
                  <Row label="CLABE (18 dígitos)" value={info.clabe} display={info.clabe_display} testId="pay-clabe" mono big />
                  <Row label="Monto" value={formatMXN(info.amount_mxn)} testId="pay-amount" />
                  <Row label="Concepto sugerido" value={info.concept_suggested} testId="pay-concept" />
                  <div className="mt-6 rounded-2xl border border-[#ccff00]/30 bg-[#ccff00]/5 p-4 text-sm text-white/80 flex items-start gap-3">
                    <Info size={16} className="text-[#ccff00] mt-0.5 flex-shrink-0" />
                    <p><strong>Después de transferir</strong>, regístralo en el formulario con la referencia o los últimos 4 dígitos. Al verificarlo activamos tu plan por {chosen.days} días (normalmente en menos de 4 horas hábiles).</p>
                  </div>
                </div>
              </motion.section>

              <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="lg:col-span-5">
                <form onSubmit={submitSpei} className="bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 md:p-8" data-testid="pay-form">
                  <div className="chip mb-4"><Landmark size={12} className="text-[#ff5a00]" /> Ya transferí</div>
                  <h3 className="font-display text-xl tracking-tight mb-6">Avísanos de tu pago</h3>
                  <Field label="Monto (MXN)">
                    <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} type="text" inputMode="decimal" className="input" data-testid="pay-amount-input" />
                  </Field>
                  <Field label="Referencia / concepto o últimos 4 dígitos">
                    <input value={ref} onChange={(e) => setRef(e.target.value)} className="input" placeholder="Ej. CHLN-3421 o BBVA-1234" data-testid="pay-reference" />
                  </Field>
                  <Field label="Nombre de quien depositó">
                    <input value={depositor} onChange={(e) => setDepositor(e.target.value)} className="input" data-testid="pay-depositor" />
                  </Field>
                  <Field label="Fecha del depósito">
                    <input value={paidAt} onChange={(e) => setPaidAt(e.target.value)} type="date" className="input" data-testid="pay-date" />
                  </Field>
                  <Field label="Notas (opcional)">
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input resize-none" data-testid="pay-notes" />
                  </Field>
                  <button type="submit" disabled={sending} className="mt-2 w-full py-4 rounded-2xl bg-[#ff5a00] hover:bg-[#ff7a30] text-black font-display text-xl tracking-tighter flex items-center justify-center gap-2 disabled:opacity-60" data-testid="pay-submit">
                    {sending ? "Enviando…" : (<>Reportar pago <ArrowRight size={18} /></>)}
                  </button>
                </form>
              </motion.section>
            </div>
          )}
        </section>

        {/* Historial */}
        <section className="mt-10" data-testid="pay-history">
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-3">/ Tus pagos</p>
          {loading && payments.length === 0 ? <p className="text-white/40 text-sm">Cargando…</p>
            : payments.length === 0 ? <p className="text-white/40 text-sm">Aún no has registrado ningún pago.</p>
            : (
              <div className="border border-white/10 rounded-2xl overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr_120px_150px_150px] px-4 py-3 bg-white/5 text-[10px] font-mono uppercase tracking-widest text-white/50">
                  <span>Referencia</span><span>Plan · método</span><span>Monto</span><span>Estado</span><span>Periodo</span>
                </div>
                {payments.map((p) => {
                  const m = STATUS[p.status] || STATUS.pending;
                  const Icon = m.icon;
                  return (
                    <div key={p.id} className="grid grid-cols-[1fr_1fr_120px_150px_150px] items-center px-4 py-3 border-t border-white/5 text-sm" data-testid={`pay-row-${p.id}`}>
                      <div><p className="truncate">{p.reference}</p><p className="text-white/40 font-mono text-[10px]">{fmtDate(p.created_at)}</p></div>
                      <div className="font-mono text-[11px] uppercase tracking-widest text-white/70">{(p.plan || "chalanito") === "onepay" ? "ONE PAY" : "CHALANITO"} · {p.method === "stripe" ? "tarjeta" : "SPEI"}</div>
                      <div className="font-display text-lg tracking-tighter">{p.amount_mxn ? formatMXN(p.amount_mxn) : `$${p.amount_usd} USD`}</div>
                      <span className={`inline-flex w-fit items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-mono uppercase tracking-widest ${m.bg} ${m.color}`}><Icon size={11} /> {m.label}</span>
                      <div className="text-[11px] text-white/60">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</div>
                    </div>
                  );
                })}
              </div>
            )}
        </section>

        <p className="mt-10 text-center text-white/40 text-[11px] font-mono tracking-widest uppercase">
          Al pagar aceptas los <a href="/legal/terminos" className="underline text-white/70 hover:text-[#ccff00]">Términos del servicio</a> · <a href="/legal/privacidad" className="underline text-white/70 hover:text-[#ccff00]">Aviso de privacidad</a>
        </p>
      </div>
      <Toaster theme="dark" position="top-center" />
    </main>
  );
}

function Row({ label, value, display, testId, mono, big }) {
  return (
    <div className="flex items-start justify-between gap-3 py-3 border-t border-white/5 first:border-t-0">
      <div>
        <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase">{label}</p>
        <p className={`${mono ? "font-mono" : "font-display"} ${big ? "text-2xl md:text-3xl tracking-tighter" : "text-lg tracking-tight"} mt-0.5`} data-testid={testId}>{display || value}</p>
      </div>
      <Copiable value={value} testId={`${testId}-copy`} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="block font-mono text-[10px] tracking-widest text-white/50 uppercase mb-2">{label}</span>
      {children}
    </label>
  );
}
